import classNames from "classnames";
import { useFormik } from "formik";
import { useState } from "react";

import { AscButton } from "../../components";
import {
  // GoogleIcon,
  CleverIcon,
  // ClasslinkIcon,
  ShibbolethIcon,
} from "../../components/AscIcons";
import AscPasswordInput from "../../components/AscInputFields/AscPasswordInput/AscPasswordInput";
import AscTextInput from "../../components/AscInputFields/AscTextInput/AscTextInput";
import AscLink from "../../components/AscLink/AscLink";
import AscHeading from "../../components/AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../components/AscTypography/AscParagraph/AscParagraph";
import AscAuthFlowLayout from "../AscAuthFlowLayout/AscAuthFlowLayout";
import AscAnimationOnScroll from "../../components/AscAnimationOnScroll/AscAnimationOnScroll";
import AscCheckBox from "../../components/AscInputFields/AscCheckBox/AscCheckBox";
import { signInInitialValues, signInSchema } from "./AscSignInView.helper";
import AscShibbolethCheck from "./AscShibbolethCheck";
import styles from "./AscSignInView.module.scss";

export interface AscSignInViewProps {
  className?: string;
  signUpLink: string;
  forgotPasswordLink: string;
  loading?: boolean;
  onSignIn: (userLoginInfo: {
    email: string;
    password: string;
    rememberMe: boolean;
  }) => void;
  googleSignInLink: string;
  cleverSignInLink: string;
  classlinkSignInLink: string;
  onShibbolethCheck: (values: { email: string }) => void;
}

export default function AscSignInView({
  className,
  signUpLink,
  forgotPasswordLink,
  googleSignInLink,
  cleverSignInLink,
  classlinkSignInLink,
  onShibbolethCheck,
  onSignIn,
  loading,
}: AscSignInViewProps) {
  const [showShibbolethEmailCheck, setShowShibbolethEmailCheck] =
    useState(false);

  const formik = useFormik({
    initialValues: signInInitialValues,
    validationSchema: signInSchema,
    onSubmit: (values) => {
      onSignIn(values);
    },
  });

  return (
    <AscAuthFlowLayout alignment="right" roundSideImage>
      {showShibbolethEmailCheck ? (
        <AscShibbolethCheck
          loading={loading}
          onShibbolethCheck={onShibbolethCheck}
          setShowShibbolethEmailCheck={setShowShibbolethEmailCheck}
        />
      ) : (
        <form
          onSubmit={formik?.handleSubmit}
          data-testid="asc-sign-in-view"
          className={classNames(styles.ground, className)}
        >
          <header className={styles.heading}>
            <AscHeading className={styles.title} level={1}>
              Welcome back
            </AscHeading>
            <AscParagraph className={styles.subTitle}>
              Happy to see you again!
            </AscParagraph>
          </header>
          <AscAnimationOnScroll className={styles.form}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="email">
                Email
              </label>
              <AscTextInput
                className={styles.inputField}
                placeholder="Enter your email"
                id="email"
                name="email"
                data-testid="asc-sign-in-email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.errors.email && formik.touched.email && (
                <div className={styles.fieldError}>{formik.errors.email}</div>
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="password">
                Password
              </label>
              <AscPasswordInput
                className={styles.inputField}
                placeholder="Enter your password"
                id="password"
                name="password"
                data-testid="asc-sign-in-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
              />
              {formik.errors.password && formik.touched.password && (
                <div className={styles.fieldError}>
                  {formik.errors.password}
                </div>
              )}
            </div>
            <div className={styles.bottomFormContent}>
              <div className={styles.rememberMeContainer}>
                <AscCheckBox
                  id="rememberMe"
                  name="rememberMe"
                  data-testid="asc-sign-in-remember-me"
                  checked={formik.values.rememberMe}
                  onChange={formik.handleChange}
                />
                <label htmlFor="rememberMe" className={styles.rememberMeText}>
                  Remember me
                </label>
              </div>
              <AscLink
                className={styles.forgotPasswordLink}
                href={forgotPasswordLink}
                innerWrapperTag="button"
              >
                Forgot Password?
              </AscLink>
            </div>
          </AscAnimationOnScroll>
          <footer className={styles.footer}>
            <AscButton
              className={styles.footerButton}
              variant="primary"
              size="large"
              fullWidth
              type="submit"
              data-testid="asc-sign-in-submit"
              loading={loading}
            >
              Sign in
            </AscButton>
            <span className={styles.or}>or</span>
            {/* <AscLink innerWrapperTag={null} href={googleSignInLink}>
            <AscButton
              className={classNames(styles.signInButton)}
              variant="outlined"
              size="large"
              fullWidth
              type="button"
              narrowPadding
            >
              <GoogleIcon className={styles.brandIcon} />{" "}
              <span className={styles.signInButtonTitle}>
                Sign in with Google
              </span>
            </AscButton>
          </AscLink> */}
            <AscLink innerWrapperTag={null} href={cleverSignInLink}>
              <AscButton
                className={classNames(styles.signInButton)}
                variant="outlined"
                size="large"
                fullWidth
                type="button"
                narrowPadding
              >
                <CleverIcon className={styles.brandIcon} />{" "}
                <span className={styles.signInButtonTitle}>
                  Sign in with Clever
                </span>
              </AscButton>
            </AscLink>
            {/* <AscLink innerWrapperTag={null} href={classlinkSignInLink}>
            <AscButton
              className={classNames(styles.signInButton)}
              variant="outlined"
              size="large"
              fullWidth
              type="button"
              narrowPadding
            >
              <ClasslinkIcon className={styles.brandIcon} />{" "}
              <span className={styles.signInButtonTitle}>
                Sign in with ClassLink
              </span>
            </AscButton>
          </AscLink> */}
            <AscButton
              className={classNames(styles.signInButton)}
              variant="outlined"
              size="large"
              fullWidth
              type="button"
              narrowPadding
              onClick={() => setShowShibbolethEmailCheck(true)}
            >
              <ShibbolethIcon className={styles.brandIcon} />{" "}
              <span className={styles.signInButtonTitle}>
                Sign in with Shibboleth
              </span>
            </AscButton>
            {/* <div className={styles.signUpPrompt}>
            Don’t have an account?{" "}
            <AscLink
              className={styles.signUpLink}
              href={signUpLink}
              innerWrapperTag="button"
            >
              Sign up
            </AscLink>
          </div> */}
          </footer>
        </form>
      )}
    </AscAuthFlowLayout>
  );
}
